import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar, MobileHeader } from '../components/shared/Sidebar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useTheme } from '../contexts/ThemeContext';
import axios from '../lib/axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Gamepad2, Plus, Trash2, X, Check, Trophy, TrendingUp, 
  DollarSign, Users, Activity, ChevronRight, History, Flame,
  Target, Crown, Shield, Star, RefreshCw, BarChart3, Eye
} from 'lucide-react';
import { formatCurrency, formatDateTime, formatRelativeTime } from '../lib/utils';

// Animated background particles
function GamingParticles({ theme }) {
  if (theme === 'light') return null;
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {[...Array(12)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 rounded-full"
          style={{
            background: ['#00F5FF', '#9945FF', '#FFD700', '#00FF88'][i % 4],
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -20, 0],
            opacity: [0.2, 0.5, 0.2],
            scale: [1, 1.5, 1],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
          }}
        />
      ))}
    </div>
  );
}

export default function ArenasPage() {
  const { theme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [games, setGames] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Selected arena for detail view
  const [selectedArena, setSelectedArena] = useState(null);
  
  // Form
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', category: '' });

  // Theme-aware colors
  const colors = {
    bg: theme === 'dark' ? '#080810' : '#f8fafc',
    card: theme === 'dark' ? '#0d0d15' : '#ffffff',
    text: theme === 'dark' ? '#ffffff' : '#1e293b',
    textMuted: theme === 'dark' ? '#9ca3af' : '#64748b',
    border: theme === 'dark' ? 'rgba(255,255,255,0.1)' : '#e2e8f0',
    accent: theme === 'dark' ? '#00F5FF' : '#0891b2',
    accentBg: theme === 'dark' ? 'rgba(0,245,255,0.1)' : 'rgba(8,145,178,0.1)',
  };

  const fetchData = useCallback(async () => {
    try {
      setRefreshing(true);
      const [gamesRes, transRes] = await Promise.all([
        axios.get('/games'),
        axios.get('/transactions', { params: { limit: 500 } })
      ]);
      setGames(gamesRes.data);
      setTransactions(transRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate arena stats
  const getArenaStats = (gameName) => {
    const arenaTrans = transactions.filter(t => t.game_name === gameName);
    const deposits = arenaTrans.filter(t => t.transaction_type === 'deposit');
    const withdrawals = arenaTrans.filter(t => t.transaction_type === 'withdrawal');
    
    const totalDeposits = deposits.reduce((sum, t) => sum + (t.deposit_amount || 0), 0);
    const totalWithdrawals = withdrawals.reduce((sum, t) => sum + (t.deposit_amount || 0), 0);
    const uniquePlayers = [...new Set(arenaTrans.map(t => t.player_id))].length;
    const uniqueStaff = [...new Set(arenaTrans.map(t => t.employee_id))].length;
    
    // Top players in this arena
    const playerTotals = {};
    deposits.forEach(t => {
      if (!playerTotals[t.player_id]) {
        playerTotals[t.player_id] = { name: t.player_name, total: 0, count: 0 };
      }
      playerTotals[t.player_id].total += t.deposit_amount || 0;
      playerTotals[t.player_id].count += 1;
    });
    const topPlayers = Object.entries(playerTotals)
      .map(([id, data]) => ({ player_id: id, ...data }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
    
    // Recent transactions
    const recentTrans = arenaTrans.slice(0, 20);
    
    return {
      totalTransactions: arenaTrans.length,
      totalDeposits,
      totalWithdrawals,
      netRevenue: totalDeposits - totalWithdrawals,
      uniquePlayers,
      uniqueStaff,
      topPlayers,
      recentTrans
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/games', formData);
      toast.success('Arena created!');
      setFormData({ name: '', category: '' });
      setShowForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create arena');
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleDelete = async (gameId) => {
    setDeleteConfirm(gameId);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`/games/${deleteConfirm}`);
      toast.success('Arena deleted');
      if (selectedArena?.id === deleteConfirm) setSelectedArena(null);
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete arena');
      setDeleteConfirm(null);
    }
  };

  // Game category icons/colors
  const getCategoryStyle = (category) => {
    const categories = {
      'Table Games': { color: '#FFD700', icon: Trophy },
      'Card Games': { color: '#FF3366', icon: Target },
      'Slots': { color: '#9945FF', icon: Star },
      'Sports': { color: '#00FF88', icon: Shield },
      'default': { color: '#00F5FF', icon: Gamepad2 }
    };
    return categories[category] || categories.default;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: colors.bg }}>
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }} className="relative">
          <div className="w-20 h-20 rounded-full border-4" style={{ borderColor: `${colors.accent}30`, borderTopColor: colors.accent }} />
          <Gamepad2 className="absolute inset-0 m-auto w-8 h-8" style={{ color: colors.accent }} />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen transition-colors duration-300" style={{ background: colors.bg }} data-testid="arenas-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <MobileHeader onMenuClick={() => setSidebarOpen(true)} />

      {/* Background Effects */}
      <GamingParticles theme={theme} />
      {theme === 'dark' && (
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#00F5FF]/5 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-[#9945FF]/5 rounded-full blur-[150px]" />
        </div>
      )}

      <main className="lg:ml-72 min-h-screen relative z-10 p-4 md:p-6 lg:p-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6"
        >
          <div className="flex items-center gap-4">
            <motion.div
              animate={theme === 'dark' ? { 
                boxShadow: ['0 0 20px #00F5FF', '0 0 40px #00F5FF', '0 0 20px #00F5FF'],
              } : {}}
              transition={{ repeat: Infinity, duration: 3 }}
              className="p-3 rounded-xl border"
              style={{ 
                background: theme === 'dark' 
                  ? 'linear-gradient(135deg, rgba(0,245,255,0.2), rgba(153,69,255,0.2))' 
                  : 'linear-gradient(135deg, rgba(8,145,178,0.1), rgba(139,92,246,0.1))',
                borderColor: theme === 'dark' ? 'rgba(0,245,255,0.3)' : 'rgba(8,145,178,0.2)'
              }}
            >
              <Gamepad2 className="w-8 h-8" style={{ color: colors.accent }} />
            </motion.div>
            <div>
              <h1 className="text-3xl md:text-4xl font-black" style={{ 
                color: colors.text,
                textShadow: theme === 'dark' ? '0 0 30px rgba(0,245,255,0.3)' : 'none'
              }}>
                ARENAS
              </h1>
              <p style={{ color: colors.textMuted }}>Game zones and performance reports</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowForm(true)}
              className="px-4 py-2.5 rounded-xl font-bold flex items-center gap-2"
              style={{ 
                background: theme === 'dark' 
                  ? 'linear-gradient(135deg, #00F5FF, #9945FF)' 
                  : 'linear-gradient(135deg, #0891b2, #8b5cf6)',
                color: theme === 'dark' ? '#000' : '#fff'
              }}
              data-testid="add-arena-btn"
            >
              <Plus className="w-4 h-4" /> NEW ARENA
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={fetchData}
              disabled={refreshing}
              className="p-2.5 rounded-xl border"
              style={{ 
                background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#fff',
                borderColor: colors.border,
                color: colors.textMuted
              }}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </motion.button>
          </div>
        </motion.div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="p-4 border relative overflow-hidden" style={{ background: colors.card, borderColor: colors.border }}>
              <div className="absolute top-0 left-0 w-full h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${colors.accent}, transparent)` }} />
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg" style={{ background: colors.accentBg }}>
                  <Gamepad2 className="w-5 h-5" style={{ color: colors.accent }} />
                </div>
                <span className="text-sm" style={{ color: colors.textMuted }}>TOTAL ARENAS</span>
              </div>
              <p className="text-3xl font-black" style={{ color: colors.text }}>{games.length}</p>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card className="p-4 border relative overflow-hidden" style={{ background: colors.card, borderColor: colors.border }}>
              <div className="absolute top-0 left-0 w-full h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #00FF88, transparent)' }} />
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg" style={{ background: 'rgba(0,255,136,0.1)' }}>
                  <DollarSign className="w-5 h-5 text-[#00FF88]" />
                </div>
                <span className="text-sm" style={{ color: colors.textMuted }}>TOTAL VOLUME</span>
              </div>
              <p className="text-3xl font-black text-[#00FF88]">
                {formatCurrency(transactions.filter(t => t.transaction_type === 'deposit').reduce((sum, t) => sum + (t.deposit_amount || 0), 0))}
              </p>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="p-4 border relative overflow-hidden" style={{ background: colors.card, borderColor: colors.border }}>
              <div className="absolute top-0 left-0 w-full h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #FFD700, transparent)' }} />
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg" style={{ background: 'rgba(255,215,0,0.1)' }}>
                  <Activity className="w-5 h-5 text-[#FFD700]" />
                </div>
                <span className="text-sm" style={{ color: colors.textMuted }}>TOTAL BATTLES</span>
              </div>
              <p className="text-3xl font-black text-[#FFD700]">{transactions.length}</p>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
            <Card className="p-4 border relative overflow-hidden" style={{ background: colors.card, borderColor: colors.border }}>
              <div className="absolute top-0 left-0 w-full h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #9945FF, transparent)' }} />
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg" style={{ background: 'rgba(153,69,255,0.1)' }}>
                  <Users className="w-5 h-5 text-[#9945FF]" />
                </div>
                <span className="text-sm" style={{ color: colors.textMuted }}>ACTIVE PLAYERS</span>
              </div>
              <p className="text-3xl font-black text-[#9945FF]">
                {[...new Set(transactions.map(t => t.player_id))].length}
              </p>
            </Card>
          </motion.div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Arenas List */}
          <div className="lg:col-span-2">
            <Card className="border overflow-hidden" style={{ background: colors.card, borderColor: colors.border }}>
              <div 
                className="absolute top-0 left-0 w-full h-[2px]"
                style={{ background: `linear-gradient(90deg, transparent, ${colors.accent}, transparent)` }}
              />
              
              <div className="p-4 border-b" style={{ borderColor: colors.border }}>
                <h3 className="font-bold flex items-center gap-2" style={{ color: colors.text }}>
                  <Trophy className="w-5 h-5 text-[#FFD700]" /> 
                  ARENA LEADERBOARD
                </h3>
              </div>

              <ScrollArea className="h-[600px]">
                <div className="p-4 space-y-3">
                  {games.map((game, idx) => {
                    const stats = getArenaStats(game.name);
                    const { color, icon: Icon } = getCategoryStyle(game.category);
                    const isSelected = selectedArena?.id === game.id;
                    
                    return (
                      <motion.div
                        key={game.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        whileHover={{ x: 5 }}
                        onClick={() => setSelectedArena(game)}
                        className="p-4 rounded-xl cursor-pointer transition-all border"
                        style={{
                          background: isSelected 
                            ? (theme === 'dark' ? `linear-gradient(135deg, ${color}15, rgba(153,69,255,0.1))` : `linear-gradient(135deg, ${color}08, rgba(139,92,246,0.05))`)
                            : (theme === 'dark' ? 'rgba(255,255,255,0.02)' : '#fff'),
                          borderColor: isSelected ? `${color}50` : colors.border
                        }}
                        data-testid={`arena-${game.id}`}
                      >
                        <div className="flex items-center gap-4">
                          {/* Rank */}
                          <div 
                            className="w-10 h-10 rounded-xl flex items-center justify-center font-black"
                            style={{ 
                              background: idx < 3 ? ['#FFD70020', '#C0C0C020', '#CD7F3220'][idx] : 'rgba(255,255,255,0.05)',
                              color: idx < 3 ? ['#FFD700', '#C0C0C0', '#CD7F32'][idx] : colors.textMuted
                            }}
                          >
                            {idx < 3 ? [<Crown key="1" className="w-5 h-5" />, <Shield key="2" className="w-5 h-5" />, <Star key="3" className="w-5 h-5" />][idx] : `#${idx + 1}`}
                          </div>
                          
                          {/* Arena Icon */}
                          <div className="p-2 rounded-lg" style={{ background: `${color}20` }}>
                            <Icon className="w-6 h-6" style={{ color }} />
                          </div>
                          
                          {/* Arena Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-bold truncate" style={{ color: colors.text }}>{game.name}</p>
                              {game.category && (
                                <Badge 
                                  className="text-xs"
                                  style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}
                                >
                                  {game.category}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm" style={{ color: colors.textMuted }}>
                              <span>{stats.totalTransactions} battles</span>
                              <span>{stats.uniquePlayers} players</span>
                            </div>
                          </div>

                          {/* Stats */}
                          <div className="text-right">
                            <p className="text-xl font-black text-[#00FF88]">{formatCurrency(stats.totalDeposits)}</p>
                            <p className="text-xs" style={{ color: colors.textMuted }}>Volume</p>
                          </div>

                          <ChevronRight className="w-5 h-5" style={{ color: colors.textMuted }} />
                          
                          {/* Delete Button */}
                          <div data-actions="true" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleDelete(game.id); }}
                              className="h-8 w-8 hover:bg-[#FF3366]/10"
                              data-testid={`delete-arena-${game.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-[#FF3366]" />
                            </Button>
                          </div>
                        </div>

                        {/* Quick Stats */}
                        <div className="mt-3 pt-3 grid grid-cols-4 gap-2 text-center" style={{ borderTop: `1px solid ${colors.border}` }}>
                          <div>
                            <p className="text-xs" style={{ color: colors.textMuted }}>Deposits</p>
                            <p className="font-bold text-[#00FF88]">{formatCurrency(stats.totalDeposits)}</p>
                          </div>
                          <div>
                            <p className="text-xs" style={{ color: colors.textMuted }}>Withdrawals</p>
                            <p className="font-bold text-[#FF3366]">{formatCurrency(stats.totalWithdrawals)}</p>
                          </div>
                          <div>
                            <p className="text-xs" style={{ color: colors.textMuted }}>Net</p>
                            <p className={`font-bold ${stats.netRevenue >= 0 ? 'text-[#00FF88]' : 'text-[#FF3366]'}`}>
                              {formatCurrency(stats.netRevenue)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs" style={{ color: colors.textMuted }}>Staff</p>
                            <p className="font-bold" style={{ color: colors.text }}>{stats.uniqueStaff}</p>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}

                  {games.length === 0 && (
                    <div className="text-center py-12" style={{ color: colors.textMuted }}>
                      <Gamepad2 className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p className="font-bold">No arenas yet</p>
                      <p className="text-sm">Click "NEW ARENA" to create your first game zone</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </Card>
          </div>

          {/* Arena Detail Panel */}
          <div>
            <Card className="border overflow-hidden sticky top-4" style={{ background: colors.card, borderColor: colors.border }}>
              <div 
                className="absolute top-0 left-0 w-full h-[2px]"
                style={{ background: 'linear-gradient(90deg, transparent, #9945FF, transparent)' }}
              />

              {selectedArena ? (
                <div className="p-4">
                  {/* Arena Header */}
                  {(() => {
                    const { color, icon: Icon } = getCategoryStyle(selectedArena.category);
                    const stats = getArenaStats(selectedArena.name);
                    
                    return (
                      <>
                        <div className="flex items-center gap-4 mb-6">
                          <div className="p-3 rounded-xl" style={{ background: `${color}20` }}>
                            <Icon className="w-8 h-8" style={{ color }} />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-xl font-bold" style={{ color: colors.text }}>{selectedArena.name}</h3>
                            {selectedArena.category && (
                              <Badge style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}>
                                {selectedArena.category}
                              </Badge>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(selectedArena.id)}
                            className="hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4 text-[#FF3366]" />
                          </Button>
                        </div>

                        {/* Tabs for Reports */}
                        <Tabs defaultValue="overview" className="space-y-4">
                          <TabsList className="grid grid-cols-3 gap-1 p-1 rounded-xl" style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f1f5f9' }}>
                            <TabsTrigger value="overview" className="text-xs rounded-lg data-[state=active]:text-white">Overview</TabsTrigger>
                            <TabsTrigger value="players" className="text-xs rounded-lg data-[state=active]:text-white">Players</TabsTrigger>
                            <TabsTrigger value="history" className="text-xs rounded-lg data-[state=active]:text-white">History</TabsTrigger>
                          </TabsList>

                          {/* Overview Tab */}
                          <TabsContent value="overview" className="space-y-4">
                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                              <div className="grid grid-cols-2 gap-3">
                                <div className="p-3 rounded-xl border" style={{ background: 'rgba(0,255,136,0.1)', borderColor: 'rgba(0,255,136,0.2)' }}>
                                  <TrendingUp className="w-4 h-4 text-[#00FF88] mb-1" />
                                  <p className="text-lg font-bold text-[#00FF88]">{formatCurrency(stats.totalDeposits)}</p>
                                  <p className="text-xs" style={{ color: colors.textMuted }}>Total Deposits</p>
                                </div>
                                <div className="p-3 rounded-xl border" style={{ background: 'rgba(255,51,102,0.1)', borderColor: 'rgba(255,51,102,0.2)' }}>
                                  <DollarSign className="w-4 h-4 text-[#FF3366] mb-1" />
                                  <p className="text-lg font-bold text-[#FF3366]">{formatCurrency(stats.totalWithdrawals)}</p>
                                  <p className="text-xs" style={{ color: colors.textMuted }}>Total Withdrawals</p>
                                </div>
                                <div className="p-3 rounded-xl border" style={{ background: `${color}15`, borderColor: `${color}30` }}>
                                  <Activity className="w-4 h-4 mb-1" style={{ color }} />
                                  <p className="text-lg font-bold" style={{ color }}>{stats.totalTransactions}</p>
                                  <p className="text-xs" style={{ color: colors.textMuted }}>Total Battles</p>
                                </div>
                                <div className="p-3 rounded-xl border" style={{ background: 'rgba(153,69,255,0.1)', borderColor: 'rgba(153,69,255,0.2)' }}>
                                  <Users className="w-4 h-4 text-[#9945FF] mb-1" />
                                  <p className="text-lg font-bold text-[#9945FF]">{stats.uniquePlayers}</p>
                                  <p className="text-xs" style={{ color: colors.textMuted }}>Unique Players</p>
                                </div>
                              </div>

                              {/* Net Revenue */}
                              <div className="p-4 rounded-xl text-center" style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f8fafc' }}>
                                <p className="text-sm" style={{ color: colors.textMuted }}>NET REVENUE</p>
                                <p className={`text-3xl font-black ${stats.netRevenue >= 0 ? 'text-[#00FF88]' : 'text-[#FF3366]'}`}>
                                  {stats.netRevenue >= 0 ? '+' : ''}{formatCurrency(stats.netRevenue)}
                                </p>
                              </div>
                            </motion.div>
                          </TabsContent>

                          {/* Top Players Tab */}
                          <TabsContent value="players">
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                              <ScrollArea className="h-[350px]">
                                <div className="space-y-2">
                                  {stats.topPlayers.length > 0 ? (
                                    stats.topPlayers.map((player, i) => (
                                      <motion.div
                                        key={player.player_id}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: i * 0.05 }}
                                        className="p-3 rounded-lg border flex items-center justify-between"
                                        style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.02)' : '#f8fafc', borderColor: colors.border }}
                                      >
                                        <div className="flex items-center gap-3">
                                          <div 
                                            className="w-8 h-8 rounded-lg flex items-center justify-center font-bold"
                                            style={{ 
                                              background: i < 3 ? ['#FFD70020', '#C0C0C020', '#CD7F3220'][i] : 'rgba(255,255,255,0.05)',
                                              color: i < 3 ? ['#FFD700', '#C0C0C0', '#CD7F32'][i] : colors.textMuted
                                            }}
                                          >
                                            #{i + 1}
                                          </div>
                                          <div>
                                            <p className="font-bold" style={{ color: colors.text }}>{player.name}</p>
                                            <p className="text-xs" style={{ color: colors.textMuted }}>{player.count} battles</p>
                                          </div>
                                        </div>
                                        <p className="font-mono font-bold text-[#00FF88]">{formatCurrency(player.total)}</p>
                                      </motion.div>
                                    ))
                                  ) : (
                                    <div className="text-center py-8" style={{ color: colors.textMuted }}>
                                      <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                                      <p>No players yet</p>
                                    </div>
                                  )}
                                </div>
                              </ScrollArea>
                            </motion.div>
                          </TabsContent>

                          {/* History Tab */}
                          <TabsContent value="history">
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                              <ScrollArea className="h-[350px]">
                                <div className="space-y-2">
                                  {stats.recentTrans.length > 0 ? (
                                    stats.recentTrans.map((trans, i) => (
                                      <motion.div
                                        key={i}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: i * 0.03 }}
                                        className="p-3 rounded-lg border"
                                        style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.02)' : '#f8fafc', borderColor: colors.border }}
                                      >
                                        <div className="flex items-center justify-between">
                                          <div>
                                            <p className="font-bold" style={{ color: colors.text }}>{trans.player_name}</p>
                                            <p className="text-xs" style={{ color: colors.textMuted }}>
                                              {trans.employee_name} • {formatRelativeTime(trans.created_at)}
                                            </p>
                                          </div>
                                          <div className="text-right">
                                            <p className={`font-mono font-bold ${trans.transaction_type === 'deposit' ? 'text-[#00FF88]' : 'text-[#FF3366]'}`}>
                                              {trans.transaction_type === 'deposit' ? '+' : '-'}{formatCurrency(trans.deposit_amount)}
                                            </p>
                                            <Badge 
                                              className="text-xs"
                                              style={{ 
                                                background: trans.status === 'approved' ? 'rgba(0,255,136,0.1)' : 'rgba(255,215,0,0.1)',
                                                color: trans.status === 'approved' ? '#00FF88' : '#FFD700'
                                              }}
                                            >
                                              {trans.status}
                                            </Badge>
                                          </div>
                                        </div>
                                      </motion.div>
                                    ))
                                  ) : (
                                    <div className="text-center py-8" style={{ color: colors.textMuted }}>
                                      <History className="w-12 h-12 mx-auto mb-2 opacity-30" />
                                      <p>No history yet</p>
                                    </div>
                                  )}
                                </div>
                              </ScrollArea>
                            </motion.div>
                          </TabsContent>
                        </Tabs>
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <motion.div 
                    animate={theme === 'dark' ? { 
                      boxShadow: ['0 0 30px rgba(0,245,255,0.2)', '0 0 60px rgba(0,245,255,0.4)', '0 0 30px rgba(0,245,255,0.2)'],
                    } : {}}
                    transition={{ repeat: Infinity, duration: 3 }}
                    className="w-20 h-20 rounded-2xl border flex items-center justify-center mx-auto mb-4"
                    style={{ 
                      background: theme === 'dark' ? 'linear-gradient(135deg, rgba(0,245,255,0.1), rgba(153,69,255,0.1))' : 'linear-gradient(135deg, rgba(8,145,178,0.05), rgba(139,92,246,0.05))',
                      borderColor: theme === 'dark' ? 'rgba(0,245,255,0.2)' : 'rgba(8,145,178,0.1)'
                    }}
                  >
                    <Eye className="w-10 h-10" style={{ color: colors.accent }} />
                  </motion.div>
                  <h3 className="text-lg font-bold mb-2" style={{ color: colors.text }}>SELECT ARENA</h3>
                  <p className="text-sm" style={{ color: colors.textMuted }}>Click on an arena to view detailed reports</p>
                </div>
              )}
            </Card>
          </div>
        </div>
      </main>

      {/* Add Arena Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: theme === 'dark' ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.5)' }}
            onClick={() => setShowForm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <Card className="p-6 border" style={{ background: colors.card, borderColor: colors.border }}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg" style={{ background: colors.accentBg }}>
                      <Gamepad2 className="w-5 h-5" style={{ color: colors.accent }} />
                    </div>
                    <h2 className="text-xl font-bold" style={{ color: colors.text }}>NEW ARENA</h2>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setShowForm(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label style={{ color: colors.textMuted }}>Arena Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Blackjack"
                      required
                      style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f8fafc', borderColor: colors.border }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label style={{ color: colors.textMuted }}>Category</Label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full rounded-lg px-3 py-2 border"
                      style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f8fafc', borderColor: colors.border, color: colors.text }}
                    >
                      <option value="">Select category...</option>
                      <option value="Table Games">Table Games</option>
                      <option value="Card Games">Card Games</option>
                      <option value="Slots">Slots</option>
                      <option value="Sports">Sports</option>
                    </select>
                  </div>
                  <div className="flex gap-2 pt-4">
                    <Button 
                      type="submit" 
                      className="flex-1 font-bold"
                      style={{ background: `linear-gradient(135deg, ${colors.accent}, #9945FF)`, color: theme === 'dark' ? '#000' : '#fff' }}
                    >
                      <Check className="w-4 h-4 mr-2" /> CREATE
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1">
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm"
            >
              <Card className="p-6 text-center" style={{ background: theme === 'dark' ? '#0d0d15' : '#fff', border: '1px solid rgba(255,51,102,0.3)' }}>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,51,102,0.2)' }}>
                  <Trash2 className="w-8 h-8 text-[#FF3366]" />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: theme === 'dark' ? '#fff' : '#000' }}>Delete Arena?</h3>
                <p className="text-sm mb-6" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>This will permanently remove this arena and all its data. This action cannot be undone.</p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setDeleteConfirm(null)}
                    data-testid="cancel-delete-arena"
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-[#FF3366] hover:bg-[#FF3366]/80 text-white"
                    onClick={confirmDelete}
                    data-testid="confirm-delete-arena"
                  >
                    Delete
                  </Button>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
